import React, { useState, useEffect, useRef } from "react";
import { 
  Box, 
  Heading, 
  Flex, 
  Accordion, 
  AccordionItem, 
  AccordionButton, 
  AccordionPanel, 
  AccordionIcon,
  Image,
  Input,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  Text,
  Button,
  Spinner,
  useToast
} from "@chakra-ui/react";
import { FaEdit, FaSave } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../utils/axios";
import StoreTable from "./components/EditStoreTable";
import StoreHoursTable from "./components/EditStoreHoursTable";
import ProductsTable from "./components/EditStoreProductsTable";
import CouponsTable from "./components/EditStoreCouponsTable";

const EditStore = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [storeImage, setStoreImage] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  const toast = useToast();

  useEffect(() => {
    fetchStoreImage();
  }, [id]);

  const fetchStoreImage = async () => {
    try {
      const response = await axiosInstance.post(`/dummy/get-store`, { store_id: id });
      
      if (response.data.status === "success" && response.data.data) {
        // The store image is in the 'logo1' field
        setStoreImage(response.data.data.logo1 || null);
      } else {
        setStoreImage(null);
      }
    } catch (error) {
      console.error("Failed to fetch store image:", error);
      setStoreImage(null);
    } finally {
      setImageLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid file type",
          description: "Please select an image file",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Validate file size (e.g., max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: "Please select an image smaller than 5MB",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select an image file first",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setUploadingImage(true);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('hstore_id', id);

      const response = await axiosInstance.post('/dummy/update-store-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success === true) {
        toast({
          title: "Store image updated successfully",
          description: response.data.message,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Refresh the store image
        setImageLoading(true);
        await fetchStoreImage();
        
        // Clear the selected file
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        toast({
          title: "Error updating store image",
          description: response.data.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to upload store image",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const MerchantImageSection = () => (
    <Box p={4} border="1px" borderColor="gray.200" borderRadius="md">
      <VStack spacing={4} align="stretch">
        {/* Current Image Display */}
        <Box>
          <Text fontWeight="semibold" mb={2}>Current Store Image:</Text>
          {imageLoading ? (
            <Spinner size="sm" />
          ) : storeImage ? (
            <Image
              src={storeImage}
              alt="Store Image"
              maxWidth="300px"
              maxHeight="300px"
              objectFit="contain"
              border="1px"
              borderColor="gray.300"
              borderRadius="md"
            />
          ) : (
            <Box
              width="300px"
              height="200px"
              bg="gray.100"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="1px"
              borderColor="gray.300"
              borderRadius="md"
            >
              <Text color="gray.500">No store image available</Text>
            </Box>
          )}
        </Box>

        {/* Upload Form */}
        <Box>
          <Text fontWeight="semibold" mb={2}>Upload New Store Image:</Text>
          <VStack spacing={3} align="stretch">
            <FormControl>
              <FormLabel>Select Image File</FormLabel>
              <Input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                p={1}
              />
            </FormControl>

            {selectedFile && (
              <Text fontSize="sm" color="gray.600">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </Text>
            )}

            <HStack spacing={2}>
              <Button
                colorScheme="blue"
                onClick={handleImageUpload}
                isLoading={uploadingImage}
                loadingText="Uploading..."
                isDisabled={!selectedFile}
                size="sm"
              >
                Upload Store Image
              </Button>
              
              {selectedFile && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  size="sm"
                >
                  Clear Selection
                </Button>
              )}
            </HStack>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );

  return (
    <Box>
      <Flex justify="space-between" flexDirection={"column"} gap={4}>
        <Heading size="lg">Edit Store details</Heading>
        <StoreTable id={id} />
        <Box width="100%" mt={8}>
          <Accordion allowToggle>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  Merchant Image
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <MerchantImageSection />
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  Coupons
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <CouponsTable id={id} />
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  Store Opening Hours
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <StoreHoursTable id={id} />
              </AccordionPanel>
            </AccordionItem>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left">
                  Products
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <ProductsTable id={id} />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      </Flex>
    </Box>
  );
};

export default EditStore;
